import { BulkPart, failureReason, MAX_LINES_PER_REQUEST, runChunked, RunChunkedOptions } from './bulkSpecs';

/** "add" keeps the quantities a part has and adds the missing ones; "replace" leaves exactly these. */
export type QuantityMode = 'add' | 'replace';

export type QuantityResultStatus = 'updated' | 'unchanged' | 'skipped' | 'failed';

/** One variation of a part (the line items sharing a quantity group). */
export interface QuantityResult {
    /** Lowest line item id of the variation. */
    id: number;
    line_item_ids?: number[];
    status: QuantityResultStatus;
    reason?: string;
    created?: number[];
    removed?: number[];
    would_create?: number[];
    would_remove?: number[];
    line_items?: Array<
        {
            id: number;
            safe_auto_quote?: boolean;
            needs_manual_quote?: boolean;
            is_pricing_in_progress?: boolean;
        } & Record<string, unknown>
    >;
}

export interface QuantitySummary {
    updated: number;
    unchanged: number;
    skipped: number;
    failed: number;
    /** Lines the request creates / removes (a preview counts what would happen). */
    linesCreated: number;
    linesRemoved: number;
    /** Of the lines created: instant price, manual quote, or still waiting for the analysis. */
    priced: number;
    manual: number;
    waiting: number;
}

export const buildQuantitiesPayload = (
    lineItemIds: number[],
    quantities: number[],
    mode: QuantityMode,
    dryRun = false
) => ({
    line_item_ids: lineItemIds,
    quantities,
    mode,
    ...(dryRun ? { dry_run: true } : {}),
});

/** Whole positive numbers, no repeats, in the order given. */
export const cleanQuantities = (values: Array<number | null | undefined>): number[] => {
    const cleaned: number[] = [];
    values.forEach(value => {
        const number = Math.trunc(Number(value));
        if (Number.isFinite(number) && number >= 1 && !cleaned.includes(number)) cleaned.push(number);
    });
    return cleaned;
};

export const summarizeQuantityResults = (results: QuantityResult[]): QuantitySummary => {
    const summary: QuantitySummary = {
        updated: 0,
        unchanged: 0,
        skipped: 0,
        failed: 0,
        linesCreated: 0,
        linesRemoved: 0,
        priced: 0,
        manual: 0,
        waiting: 0,
    };
    results.forEach(result => {
        summary[result.status] += 1;
        if (result.status !== 'updated') return;
        summary.linesCreated += result.created?.length ?? result.would_create?.length ?? 0;
        summary.linesRemoved += result.removed?.length ?? result.would_remove?.length ?? 0;
        const created = new Set(result.created ?? []);
        (result.line_items ?? [])
            .filter(item => created.has(item.id))
            .forEach(item => {
                if (item.safe_auto_quote && !item.needs_manual_quote) summary.priced += 1;
                else if (item.is_pricing_in_progress) summary.waiting += 1;
                else summary.manual += 1;
            });
    });
    return summary;
};

const lines = (count: number) => `${count} ${count === 1 ? 'line' : 'lines'}`;

/** "24 lines will be created · 38 lines will be removed · 3 already match · 2 skipped". */
export const describeQuantityPreview = (summary: QuantitySummary): string => {
    const parts: string[] = [];
    if (summary.linesCreated) parts.push(`${lines(summary.linesCreated)} will be created`);
    if (summary.linesRemoved) parts.push(`${lines(summary.linesRemoved)} will be removed`);
    if (summary.unchanged) parts.push(`${summary.unchanged} already match`);
    if (summary.skipped) parts.push(`${summary.skipped} skipped`);
    return parts.join(' · ');
};

/** "24 lines created · 38 removed · 20 priced instantly · 4 manual quote". */
export const describeQuantitySummary = (summary: QuantitySummary): string => {
    const parts: string[] = [];
    if (summary.linesCreated) parts.push(`${lines(summary.linesCreated)} created`);
    if (summary.linesRemoved) parts.push(`${summary.linesRemoved} removed`);
    if (summary.priced) parts.push(`${summary.priced} priced instantly`);
    if (summary.manual) parts.push(`${summary.manual} manual quote`);
    if (summary.waiting) parts.push(`${summary.waiting} waiting for analysis`);
    if (summary.unchanged) parts.push(`${summary.unchanged} already matched`);
    if (summary.skipped) parts.push(`${summary.skipped} skipped`);
    if (summary.failed) parts.push(`${summary.failed} failed`);
    return parts.join(' · ');
};

/**
 * Lines a quantities request may carry. The server counts the new lines (lines x quantities)
 * against its cap, so a preview or an apply that sent parts by count alone could be refused.
 */
export const linesPerRequestForQuantities = (quantityCount: number): number =>
    Math.max(1, Math.floor(MAX_LINES_PER_REQUEST / Math.max(1, quantityCount)));

export type RunBulkQuantitiesOptions = Omit<RunChunkedOptions<QuantityResult>, 'failed'>;

/** Applies the quantities request by request (see `runChunked`). */
export const runBulkQuantities = (options: RunBulkQuantitiesOptions): Promise<QuantityResult[]> =>
    runChunked<QuantityResult>({
        ...options,
        failed: (ids, reason) => ids.map(id => ({ id, line_item_ids: [id], status: 'failed' as const, reason })),
    });

/** Parts that still have a variation that failed, to retry only those. */
export const failedQuantityParts = (parts: BulkPart[], results: QuantityResult[]): BulkPart[] => {
    const failed = new Set(results.filter(r => r.status === 'failed').flatMap(r => r.line_item_ids ?? [r.id]));
    return parts.filter(part => part.lineItemIds.some(id => failed.has(id)));
};

export { failureReason };
